'use client'

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Truck } from 'lucide-react'

import { homeDefaults, type HomePageContent } from '@/lib/content/defaults/home'
import type { Suburb } from '@/lib/content/defaults/site-content'
import { ServiceAreaMap } from '@/components/service-area-map'

/** Fisher-Yates shuffle (returns new array) */
function shuffle<T>(arr: readonly T[]): T[] {
  const out = [...arr]
  for (let i = out.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1))
    ;[out[i], out[j]] = [out[j], out[i]]
  }
  return out
}

const CYCLE_MS = 3000

interface ServiceAreaCardProps {
  suburbs: Suburb[]
  /** Defaults to the home copy so other pages render the card unchanged. */
  content?: HomePageContent['crew']['serviceArea']
}

export function ServiceAreaCard({
  suburbs,
  content = homeDefaults.crew.serviceArea,
}: ServiceAreaCardProps) {
  const mapSuburbs = useMemo(
    () => suburbs.filter((s): s is Suburb & { x: number; y: number } => s.x != null && s.y != null),
    [suburbs]
  )
  const home = useMemo(
    () => mapSuburbs.find((s) => s.isHome)?.name ?? mapSuburbs[0]?.name ?? '',
    [mapSuburbs]
  )

  const [active, setActive] = useState<string>(home)
  const [paused, setPaused] = useState(false)
  const [visible, setVisible] = useState(false)
  const cardRef = useRef<HTMLDivElement>(null)
  const orderRef = useRef<string[]>([])
  const indexRef = useRef(0)
  const resumeTimer = useRef<ReturnType<typeof setTimeout>>(null)

  // Build a random cycle order on mount, always starting with home
  useEffect(() => {
    const names = mapSuburbs.map((s) => s.name)
    const rest = shuffle(names.filter((n) => n !== home))
    orderRef.current = home ? [home, ...rest] : names
    indexRef.current = 0
  }, [mapSuburbs, home])

  // Track visibility so we can pause cycling off-screen
  useEffect(() => {
    const el = cardRef.current
    if (!el) return
    const observer = new IntersectionObserver(([entry]) => setVisible(entry.isIntersecting), {
      threshold: 0.1,
    })
    observer.observe(el)
    return () => observer.disconnect()
  }, [])

  // Auto-cycle when not paused and visible
  useEffect(() => {
    if (paused || !visible || orderRef.current.length === 0) return
    const id = setInterval(() => {
      indexRef.current = (indexRef.current + 1) % orderRef.current.length
      setActive(orderRef.current[indexRef.current])
    }, CYCLE_MS)
    return () => clearInterval(id)
  }, [paused, visible])

  const handleSelect = useCallback((name: string) => {
    setActive(name)
    setPaused(true)

    // Find the selected town in the cycle order so it resumes from there
    const idx = orderRef.current.indexOf(name)
    if (idx !== -1) indexRef.current = idx

    // Resume auto-cycle after 6s of inactivity
    if (resumeTimer.current) clearTimeout(resumeTimer.current)
    resumeTimer.current = setTimeout(() => setPaused(false), 6000)
  }, [])

  // Clean up resume timer
  useEffect(() => {
    return () => {
      if (resumeTimer.current) clearTimeout(resumeTimer.current)
    }
  }, [])

  return (
    <div
      ref={cardRef}
      className="card-glow relative grid items-stretch gap-0 overflow-hidden rounded-sm border border-white/10 bg-[#140a1a] lg:grid-cols-2"
    >
      <div className="flex flex-col justify-center p-8 lg:p-10">
        <h3 className="font-display flex items-center gap-3 text-3xl font-bold text-white sm:text-4xl">
          <Truck className="text-brand-lavender size-8 shrink-0 sm:size-10" aria-hidden="true" />
          {content.heading}
        </h3>
        <p className="mt-4 text-base leading-relaxed text-white/60">{content.body}</p>
        <p className="mt-4 text-base text-white/60">{content.callLine}</p>
        <div className="mt-6 flex flex-wrap gap-2">
          {mapSuburbs.map((suburb) => (
            <button
              key={suburb.name}
              type="button"
              onClick={() => handleSelect(suburb.name)}
              className={`font-heading cursor-pointer rounded-sm border px-2.5 py-1 text-xs font-bold tracking-wider uppercase transition-all duration-200 ${
                active === suburb.name
                  ? 'border-[#8bfa6e]/40 bg-[#8bfa6e]/15 text-[#8bfa6e] shadow-[0_0_8px_rgba(149,248,98,0.2)]'
                  : 'border-white/10 bg-white/5 text-white/50 hover:border-white/20 hover:text-white/70'
              }`}
            >
              {suburb.name}
            </button>
          ))}
        </div>
      </div>
      <div className="relative lg:min-h-0">
        <ServiceAreaMap
          locations={mapSuburbs}
          activeLocation={active}
          onSelectLocation={handleSelect}
        />
      </div>
    </div>
  )
}
